import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SecretInput } from './forms/shared';
import { newPairId, type KeyValuePair } from './key-value-pairs';

interface Props {
  value: KeyValuePair[];
  onChange: (pairs: KeyValuePair[]) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  secretValues?: boolean;
}

export function KeyValueEditor({
  value,
  onChange,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
  secretValues = false,
}: Props): JSX.Element {
  const update = (id: string, field: 'key' | 'value', val: string): void => {
    onChange(value.map((p) => (p.id === id ? { ...p, [field]: val } : p)));
  };

  const remove = (id: string): void => {
    onChange(value.filter((p) => p.id !== id));
  };

  const add = (): void => {
    onChange([...value, { id: newPairId(), key: '', value: '' }]);
  };

  return (
    <div className="space-y-2">
      {value.map((pair) => (
        <div key={pair.id} className="flex items-center gap-2">
          <Input
            value={pair.key}
            onChange={(e) => update(pair.id, 'key', e.target.value)}
            placeholder={keyPlaceholder}
            className="flex-1 font-mono text-xs"
          />
          {secretValues ? (
            <div className="flex-1">
              <SecretInput
                id={`kv-val-${pair.id}`}
                value={pair.value}
                onChange={(v) => update(pair.id, 'value', v)}
                placeholder={valuePlaceholder}
              />
            </div>
          ) : (
            <Input
              value={pair.value}
              onChange={(e) => update(pair.id, 'value', e.target.value)}
              placeholder={valuePlaceholder}
              className="flex-1 font-mono text-xs"
            />
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => remove(pair.id)}
            className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1.5 text-xs">
        <Plus className="h-3 w-3" />
        Add
      </Button>
    </div>
  );
}
